import React, { useState } from 'react';
import styled from 'styled-components';

import ContextMenu, { Break, Option } from 'kingpin/atoms/ContextMenu';

import BlockMenuItems, { hasBlockMenuItems } from '../BlockMenuItems';
import { isTextElement } from '../compositeUtils';
import { NO_DRAG_CLASS, STUDIO_ELEMENT_ID_ATTR } from '../constants';
import MenuEventBoundary from '../MenuEventBoundary';

// Editing in Studio: what an element's menu does to it (see CompositeCard)
export interface ElementEditActions {
  // Selects the element, opening its settings
  onEdit: () => void;
  onOpenSettings: (section: Studio.SettingsSection) => void;
  onDuplicate: () => void;
  // Absent where the block can't be left (fullscreen)
  onDetach?: () => void;
  onRemove: () => void;
}

// The menu's class, so a selected row can hide its elements' menus (its own
// Delete takes the corner; see CompositeCard)
export const ELEMENT_MENU_CLASS = 'studio-element-menu';

// A floating menu's size: a small button, its padding and border
const FLOAT_SIZE = 26;

// Shown while the element's hovered (or its menu is open). Editing in Studio
// it floats on a raised surface centred on the top right corner of the
// cell's outline, mostly outside the cell (the corner has no resize handle
// for it; see CompositeCard). Where the corner is on the block's edge there's
// no room past it (the block clips it): in the block's first row with its
// header hidden it hangs below the top instead, and at the end of a row it
// stays left of the side. Otherwise (a dashboard or report) it sits in the top right
// corner, left of its popup button when it has one.
const MenuCorner = styled.div<{
  $isOpen: boolean;
  $hasPopup: boolean;
  $isFloating: boolean;
  $isInside: boolean;
  $isAtBlockSide: boolean;
  // The cell's outline's inset from its sides (see CompositeCard)
  $insetX: number;
}>`
  position: absolute;
  z-index: 4;
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  transition: opacity 0.2s;
  ${(props) =>
    props.$isFloating
      ? `
    top: ${props.$isInside ? 4 : -FLOAT_SIZE / 2}px;
    right: ${props.$isAtBlockSide ? 2 : props.$insetX - FLOAT_SIZE / 2}px;
    display: flex;
    padding: 2px;
    border-radius: 8px;
    border: 1px solid var(--color-border-primary);
    background-color: var(--color-bg-primary);
    box-shadow: var(--shadow-dropdown);
  `
      : `
    top: 2px;
    right: ${props.$hasPopup ? 26 : 2}px;
  `}

  [${STUDIO_ELEMENT_ID_ATTR}]:hover &,
  &:focus-within {
    opacity: 1;
  }
`;

// A Composite element's ⋮ menu. Editing in Studio, it has what a block's
// does, for the element: edit, its dates and filters (not Text's), duplicate,
// detach and remove. Its kind's own options (see BlockMenuItems, e.g. a
// Table's export) sit before Remove, and are all it has on a dashboard or
// report. Under the element's scopes (BlockMenuScope, dates and filters), so
// they see what its body draws.
const ElementMenu = ({
  element,
  name,
  w,
  h,
  hasPopup,
  editActions,
  isInside = false,
  isAtBlockSide = false,
  insetX = 0,
}: {
  element: Studio.CompositeElement;
  name: string;
  // The Composite's grid size
  w: number;
  h: number;
  hasPopup: boolean;
  editActions?: ElementEditActions;
  // In the block's first row with its header hidden, and at the end of a
  // row (see MenuCorner)
  isInside?: boolean;
  isAtBlockSide?: boolean;
  insetX?: number;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasOwnItems = hasBlockMenuItems({ content: element, isOnView: false });
  if (!editActions && !hasOwnItems) {
    return null;
  }
  const isText = isTextElement(element);
  const testId = (key: string) => `studio-element-${key}-${element.id}`;
  const ownItems = hasOwnItems && (
    <BlockMenuItems content={element} name={name} w={w} h={h} />
  );

  return (
    <MenuCorner
      className={`${NO_DRAG_CLASS} ${ELEMENT_MENU_CLASS}`}
      $isOpen={isOpen}
      $hasPopup={hasPopup}
      $isFloating={!!editActions}
      $isInside={isInside}
      $isAtBlockSide={isAtBlockSide}
      $insetX={insetX}
      // Opening it doesn't select the element (the settings panel would
      // reflow the canvas under the menu)
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <ContextMenu
        icon="kebab-outline"
        buttonType="Ghost"
        tooltip="Actions"
        testId={`studio-element-menu-${element.id}`}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
      >
        <MenuEventBoundary>
          {editActions ? (
            <>
              <Option
                label="Edit"
                icon="edit-outline"
                onClick={editActions.onEdit}
                testId={testId('edit')}
              />
              {!isText && (
                <>
                  <Option
                    label={
                      element.dateSettings ? 'Date range (own)' : 'Date range'
                    }
                    icon="calendar-outline"
                    onClick={() => editActions.onOpenSettings('dates')}
                    testId={testId('dates')}
                  />
                  <Option
                    label={
                      (element.filters ?? []).length > 0
                        ? 'Filters (applied)'
                        : 'Filters'
                    }
                    icon="filters-outline"
                    onClick={() => editActions.onOpenSettings('filters')}
                    testId={testId('filters')}
                  />
                </>
              )}
              <Option
                label="Duplicate"
                icon="copy-outline"
                onClick={editActions.onDuplicate}
                testId={testId('duplicate')}
              />
              {editActions.onDetach && (
                <Option
                  label="Detach into its own block"
                  icon="open-link"
                  onClick={editActions.onDetach}
                  testId={testId('detach')}
                />
              )}
              {ownItems && (
                <>
                  <Break />
                  {ownItems}
                </>
              )}
              <Break />
              <Option
                label="Remove"
                icon="cross-outline"
                isDanger
                onClick={editActions.onRemove}
                testId={testId('remove')}
              />
            </>
          ) : (
            ownItems
          )}
        </MenuEventBoundary>
      </ContextMenu>
    </MenuCorner>
  );
};

export default ElementMenu;
